import React from 'react';
import { 
  IoMdTrendingUp, 
  IoMdTrendingDown, 
  IoMdTime, 
  IoMdChatbubbles,
  IoMdCall,
  IoMdArrowUp,
  IoMdArrowDown,
  IoMdStats
} from 'react-icons/io';

export function InsightsTab() {
  // Dummy data for insights
  const insightsData = {
    engagement: {
      responseRate: 87.5,
      avgResponseTime: '2.3 min',
      totalMessages: 1247,
      activeConversations: 23
    },
    communication: {
      messagesSent: 892,
      messagesReceived: 355,
      callsMade: 45,
      callsReceived: 32
    },
    trends: [
      { period: 'Mon', messages: 120, calls: 8 },
      { period: 'Tue', messages: 145, calls: 12 },
      { period: 'Wed', messages: 98, calls: 6 },
      { period: 'Thu', messages: 167, calls: 15 },
      { period: 'Fri', messages: 134, calls: 10 },
      { period: 'Sat', messages: 89, calls: 5 },
      { period: 'Sun', messages: 76, calls: 4 },
    ],
    peakHours: [
      { hour: '9 AM', activity: 45 },
      { hour: '10 AM', activity: 78 },
      { hour: '11 AM', activity: 92 },
      { hour: '12 PM', activity: 105 },
      { hour: '1 PM', activity: 88 },
      { hour: '2 PM', activity: 76 },
      { hour: '3 PM', activity: 65 },
      { hour: '4 PM', activity: 54 },
    ],
    sentiment: {
      positive: 68,
      neutral: 25,
      negative: 7
    }
  };

  const StatCard = ({ 
    title, 
    value, 
    change, 
    trend, 
    icon: Icon, 
    color = 'blue' 
  }: { 
    title: string; 
    value: string | number; 
    change?: string; 
    trend?: 'up' | 'down'; 
    icon: any;
    color?: string;
  }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200',
      orange: 'bg-orange-50 text-orange-600 border-orange-200',
    };

    return (
      <div className={`rounded-xl border-2 p-6 ${colorClasses[color as keyof typeof colorClasses] || colorClasses.blue}`}>
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-lg bg-white/50`}>
            <Icon className="w-6 h-6" />
          </div>
          {change && trend && (
            <div className={`flex items-center space-x-1 text-sm font-medium ${
              trend === 'up' ? 'text-green-600' : 'text-red-600'
            }`}>
              {trend === 'up' ? <IoMdArrowUp /> : <IoMdArrowDown />}
              <span>{change}</span>
            </div>
          )}
        </div>
        <div className="text-3xl font-bold mb-1">{value}</div>
        <div className="text-sm opacity-80">{title}</div>
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Customer Insights</h2>
            <p className="text-gray-600 mt-1">Analytics and performance metrics for this customer</p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <IoMdTime className="w-4 h-4" />
            <span>Last updated: 2 minutes ago</span>
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Response Rate"
            value={`${insightsData.engagement.responseRate}%`}
            change="+5.2%"
            trend="up"
            icon={IoMdTrendingUp}
            color="green"
          />
          <StatCard
            title="Avg Response Time"
            value={insightsData.engagement.avgResponseTime}
            change="-12%"
            trend="up"
            icon={IoMdTime}
            color="blue"
          />
          <StatCard
            title="Total Messages"
            value={insightsData.engagement.totalMessages.toLocaleString()}
            change="+18%"
            trend="up"
            icon={IoMdChatbubbles}
            color="purple"
          />
          <StatCard
            title="Active Conversations"
            value={insightsData.engagement.activeConversations}
            change="+3"
            trend="up"
            icon={IoMdCall}
            color="orange"
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Activity Trend Chart */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Weekly Activity Trend</h3>
              <IoMdStats className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              {insightsData.trends.map((day, idx) => {
                const maxMessages = Math.max(...insightsData.trends.map(d => d.messages));
                const maxCalls = Math.max(...insightsData.trends.map(d => d.calls));
                return (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">{day.period}</span>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>📨 {day.messages}</span>
                        <span>📞 {day.calls}</span>
                      </div>
                    </div>
                    <div className="flex items-end space-x-2 h-8">
                      <div 
                        className="bg-blue-500 rounded-t flex-1 transition-all"
                        style={{ height: `${(day.messages / maxMessages) * 100}%` }}
                        title={`${day.messages} messages`}
                      />
                      <div 
                        className="bg-green-500 rounded-t flex-1 transition-all"
                        style={{ height: `${(day.calls / maxCalls) * 100}%` }}
                        title={`${day.calls} calls`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-center space-x-4 mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span className="text-xs text-gray-600">Messages</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span className="text-xs text-gray-600">Calls</span>
              </div>
            </div>
          </div>

          {/* Peak Hours Chart */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Peak Activity Hours</h3>
              <IoMdTime className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-3">
              {insightsData.peakHours.map((hour, idx) => {
                const maxActivity = Math.max(...insightsData.peakHours.map(h => h.activity));
                return (
                  <div key={idx} className="flex items-center space-x-3">
                    <div className="w-16 text-xs text-gray-600 font-medium">{hour.hour}</div>
                    <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-full rounded-full transition-all flex items-center justify-end pr-2"
                        style={{ width: `${(hour.activity / maxActivity) * 100}%` }}
                      >
                        <span className="text-xs text-white font-medium">{hour.activity}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Communication Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Message Breakdown</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Messages Sent</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {insightsData.communication.messagesSent}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-blue-500 h-3 rounded-full"
                    style={{ width: '71.5%' }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Messages Received</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {insightsData.communication.messagesReceived}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-green-500 h-3 rounded-full"
                    style={{ width: '28.5%' }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Call Activity</h3>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Calls Made</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {insightsData.communication.callsMade}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-purple-500 h-3 rounded-full"
                    style={{ width: '58.4%' }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Calls Received</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {insightsData.communication.callsReceived}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-orange-500 h-3 rounded-full"
                    style={{ width: '41.6%' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sentiment Analysis */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-gray-200 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sentiment Analysis</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">
                {insightsData.sentiment.positive}%
              </div>
              <div className="text-sm text-gray-600">Positive</div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${insightsData.sentiment.positive}%` }}
                />
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-gray-600 mb-2">
                {insightsData.sentiment.neutral}%
              </div>
              <div className="text-sm text-gray-600">Neutral</div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-gray-500 h-2 rounded-full"
                  style={{ width: `${insightsData.sentiment.neutral}%` }}
                />
              </div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-red-600 mb-2">
                {insightsData.sentiment.negative}%
              </div>
              <div className="text-sm text-gray-600">Negative</div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-red-500 h-2 rounded-full"
                  style={{ width: `${insightsData.sentiment.negative}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">1.2K</div>
            <div className="text-xs text-gray-500 mt-1">Total Interactions</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">94%</div>
            <div className="text-xs text-gray-500 mt-1">Satisfaction Rate</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">4.8</div>
            <div className="text-xs text-gray-500 mt-1">Avg Rating</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">12</div>
            <div className="text-xs text-gray-500 mt-1">Days Active</div>
          </div>
        </div>
      </div>
    </div>
  );
}
